import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight } from 'lucide-react';
import { LatexRenderer } from '../../ui/LatexRenderer';
interface AnswerComparisonProps {
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact';
}
/**
 * AnswerComparison — polished answer reveal with smooth entrance.
 * Correct: single emerald card with large checkmark.
 * Wrong: user answer (rose) + arrow + correct answer (emerald) side by side.
 */
export function AnswerComparison({
  userAnswer,
  correctAnswer,
  isCorrect,
  mode = 'full'
}: AnswerComparisonProps) {
  if (mode === 'compact') {
    return <div className="grid gap-2" data-id="element-4083">
        <div className={`p-3 rounded-xl border-2 ${isCorrect ? 'border-emerald-200 bg-emerald-50' : 'border-rose-100 bg-rose-50'}`} data-id="element-4084">
          <div className="text-[10px] font-black uppercase mb-1 opacity-60" data-id="element-4085">
            Jouw antwoord
          </div>
          <div className="font-bold text-slate-900 text-sm" data-id="element-4086">
            <LatexRenderer content={userAnswer} data-id="element-4087" />
          </div>
        </div>

        {!isCorrect && <div className="p-3 rounded-xl border-2 border-emerald-200 bg-emerald-50" data-id="element-4088">
            <div className="text-[10px] font-black uppercase mb-1 text-emerald-700" data-id="element-4089">
              Goede antwoord
            </div>
            <div className="font-bold text-emerald-900 text-sm" data-id="element-4090">
              <LatexRenderer content={correctAnswer} data-id="element-4091" />
            </div>
          </div>}
      </div>;
  }
  if (isCorrect) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1]
    }} className="bg-emerald-50 rounded-3xl p-6 border-2 border-emerald-200 shadow-sm" data-id="element-4092">
        <div className="flex items-center gap-4" data-id="element-4093">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: [0, 1.2, 1]
        }} transition={{
          delay: 0.1,
          duration: 0.4,
          times: [0, 0.6, 1]
        }} className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-md flex-shrink-0" data-id="element-4094">
            <Check className="w-7 h-7 text-white" strokeWidth={3} data-id="element-4095" />
          </motion.div>
          <div data-id="element-4096">
            <div className="text-xs font-black uppercase tracking-wider text-emerald-600 mb-1" data-id="element-4097">
              Jouw antwoord
            </div>
            <div className="font-black text-2xl text-emerald-900" data-id="element-4098">
              <LatexRenderer content={userAnswer} data-id="element-4099" />
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Wrong — show user answer and correct answer with arrow between
  return <div className="flex flex-col md:flex-row gap-3 items-stretch" data-id="element-4100">
      {/* User's wrong answer */}
      <motion.div initial={{
      opacity: 0,
      x: -16
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex-1" data-id="element-4101">
        <div className="bg-rose-50 rounded-3xl p-6 border-2 border-rose-200 h-full shadow-sm" data-id="element-4102">
          <div className="flex items-center gap-3 mb-3" data-id="element-4103">
            <div className="w-9 h-9 rounded-xl bg-rose-200 flex items-center justify-center text-rose-700 shadow-sm flex-shrink-0" data-id="element-4104">
              <X className="w-5 h-5" strokeWidth={3} data-id="element-4105" />
            </div>
            <span className="text-xs font-black uppercase tracking-wider text-rose-600" data-id="element-4106">
              Jouw antwoord
            </span>
          </div>
          <div className="font-black text-2xl text-rose-900 pl-12" data-id="element-4107">
            <LatexRenderer content={userAnswer} data-id="element-4108" />
          </div>
        </div>
      </motion.div>

      {/* Arrow separator */}
      <motion.div initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.15,
      duration: 0.3
    }} className="hidden md:flex items-center justify-center flex-shrink-0" data-id="element-4109">
        <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center" data-id="element-4110">
          <ArrowRight className="w-5 h-5 text-slate-400" strokeWidth={2.5} data-id="element-4111" />
        </div>
      </motion.div>

      {/* Correct answer */}
      <motion.div initial={{
      opacity: 0,
      x: 16
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.1,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex-1" data-id="element-4112">
        <div className="bg-emerald-50 rounded-3xl p-6 border-2 border-emerald-200 h-full shadow-sm" data-id="element-4113">
          <div className="flex items-center gap-3 mb-3" data-id="element-4114">
            <div className="w-9 h-9 rounded-xl bg-emerald-200 flex items-center justify-center text-emerald-700 shadow-sm flex-shrink-0" data-id="element-4115">
              <Check className="w-5 h-5" strokeWidth={3} data-id="element-4116" />
            </div>
            <span className="text-xs font-black uppercase tracking-wider text-emerald-600" data-id="element-4117">
              Het juiste antwoord
            </span>
          </div>
          <div className="font-black text-2xl text-emerald-900 pl-12" data-id="element-4118">
            <LatexRenderer content={correctAnswer} data-id="element-4119" />
          </div>
        </div>
      </motion.div>
    </div>;
}